import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  MailOutlined,
} from '@ant-design/icons';
import { Popover, Row, Badge, Tabs, Spin } from 'antd';

import Debouncer from '../../../helpers/Debouncer';
import Permissions from '../../../auth/Permissions';

import config from '../../../config';

import NotificationItem from './NotificationItem';

import { markAsRead } from '../../../notifications/state/notifications.actions';
import {
  getFormApprovalSummary,
  getAssignedForms,
  getDrafts,
} from '../../../forms/state/forms.actions';
import { getIdMap } from '../../../helpers/helpers';

import { getMyCardTags } from '../../../boards/state/boards.actions';
import { initialsFromName } from '../../../helpers/users';
import { getMyProjectTags, markProjectTagsAsRead, getMyProjectGroupTags, markProjectGroupTagsAsRead } from '../../../projects/state/projects.actions';
import { getMyBucketTags, markBucketTagsAsRead, getBuckets } from '../../../buckets/state/buckets.actions';
import { getMyEquipmentTags, markEquipmentTagsAsRead } from '../../../equipment/state/equipment.actions';
import { getMyVendorTags, markVendorTagsAsRead } from '../../../contacts/vendors/state/vendors.actions';
import { ReactComponent as BellIcon } from '../../../assets/icons/bell-icon.svg';
import { getMyCustomerTags, markCustomerTagsAsRead } from '../../../contacts/customers/state/customers.actions';
import { getMyUserTags, markUserTagsAsRead } from '../../../users/state/users.actions';

const CUSTOMER_ROUTE = '/contacts/customers';

const decorateTags = ({
  tags = [],
  userMap = {},
  read,
  authorIdProperty = 'cardNoteAuthorId',
}) => (
  tags.map((tag) => {
    const authorId = tag[authorIdProperty];
    const {
      [authorId]: { name: author } = {},
    } = userMap;
    return { ...tag, author, read };
  })
);

const debouncer = new Debouncer();
const tabStyle = {
  height: 500,
  width: 400,
  overflowY: 'auto',
};
export const NotificationsDropdown = connect(
  (state, ownProps) => {
    return {
      ...ownProps,
      notifications: state.notifications.notifications,
      approvals: state.approvals.approvals,
      unreadCount: state.approvals.approvalCount + state.notifications.notifications.filter(n => !n.read).length,
      users: state.users.users,
      formApprovals: state.forms.approvalSummary,
      cardTags: state.boards.myCardTags,
      projectTags: state.projects.myProjectTags,
      projectGroupTags: state.projects.myProjectGroupTags,
      bucketTags: state.buckets.myBucketTags,
      equipmentTags: state.equipment.myEquipmentTags,
      vendorTags: state.vendors.myVendorTags || [],
      unreadClientComms: state.customers.unreadClientComms,
      myCustomerTags: state.customers.myCustomerTags || [],
      myUserTags: state.users.myUserTags || [],
    };
  },
  {
    markAsRead,
    getFormApprovalSummary,
    getAssignedForms,
    getDrafts,
    getMyProjectTags,
    markProjectTagsAsRead,
    getMyProjectGroupTags,
    markProjectGroupTagsAsRead,
    getBuckets,
    getMyBucketTags,
    markBucketTagsAsRead,
    getMyEquipmentTags,
    markEquipmentTagsAsRead,
    getMyVendorTags,
    markVendorTagsAsRead,
    getMyCustomerTags,
    markCustomerTagsAsRead,
    getMyUserTags,
    markUserTagsAsRead,
  },
)(({
  notifications,
  approvals,
  unreadCount = 0,
  users,
  markAsRead,
  formApprovals = {},
  getFormApprovalSummary,
  getAssignedForms,
  getDrafts,
  getBuckets,
  cardTags = [],
  unreadClientComms = [],
  projectTags = [],
  projectGroupTags = [],
  bucketTags = [],
  equipmentTags = [],
  vendorTags = [],
  myCustomerTags = [],
  myUserTags = [],
}) => {
  const dispatch = useDispatch();
  const myReadCardTags = useSelector(state => state.boards.myReadCardTags);
  const company = useSelector((state) => state.settings.company);
  const hasRedesign = company?.settings?.uxRedesignPt1;
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const hasFormsPerms = Permissions.has('FORMS_READ');
  const shouldShowForms = config.showForms && hasFormsPerms;
  const formApprovalList = shouldShowForms ? Object.values(formApprovals) : [];
  const notificatonCount = unreadCount + formApprovalList.length + cardTags.length + projectTags.length
  + projectGroupTags.length + bucketTags.length + equipmentTags.length
  + vendorTags.length + unreadClientComms.length + myCustomerTags.length + myUserTags.length;
  const closeDropdown = useCallback(() => setVisible(false), []);
  const onTabChanged = useCallback(async (tabKey) => {
    if (tabKey === 'read') {
      setLoading(true);
      await debouncer.debounce(() => {
        dispatch(getMyCardTags({ hasRead: true }));
        dispatch(getMyProjectTags({ hasRead: true }));
        dispatch(getMyProjectGroupTags({ hasRead: true }));
        dispatch(getMyBucketTags({ hasRead: true }));
        dispatch(getMyEquipmentTags({ hasRead: true }));
        dispatch(getMyVendorTags({ hasRead: true }));
        dispatch(getMyCustomerTags({ hasRead: true }));
        dispatch(getMyUserTags({ hasRead: true }));
      }, 500);
      setLoading(false);
    }
  }, [dispatch]);

  const userMap = useMemo(() => getIdMap(users), [users]);
  const decoratedCardTags = useMemo(() => (
    decorateTags({ tags: cardTags, userMap, read: false })
  ), [cardTags, userMap]);
  const decoratedReadTags = useMemo(() => (
    decorateTags({ tags: myReadCardTags, userMap, read: true })
  ), [myReadCardTags, userMap]);
  const myReadProjectTags = useSelector(state => state.projects.myReadProjectTags || []);
  const decoratedProjectTags = useMemo(() => (
    decorateTags({ tags: projectTags || [], userMap, read: false, authorIdProperty: 'projectNotesAuthorId' })
  ), [projectTags, userMap]);
  const decoratedReadProjectTags = useMemo(() => (
    decorateTags({ tags: myReadProjectTags || [], userMap, read: true, authorIdProperty: 'projectNotesAuthorId' })
  ), [myReadProjectTags, userMap]);
  const myReadProjectGroupTags = useSelector(state => state.projects.myReadProjectGroupTags || []);
  const decoratedProjectGroupTags = useMemo(() => (
    decorateTags({ tags: projectGroupTags || [], userMap, read: false, authorIdProperty: 'projectGroupNotesAuthorId' })
  ), [projectGroupTags, userMap]);
  const decoratedReadProjectGroupTags = useMemo(() => (
    decorateTags({ tags: myReadProjectGroupTags || [], userMap, read: true, authorIdProperty: 'projectGroupNotesAuthorId' })
  ), [myReadProjectGroupTags, userMap]);
  const myReadBucketTags = useSelector(state => state.buckets.myReadBucketTags || []);
  const decoratedBucketTags = useMemo(() => (
    decorateTags({ tags: bucketTags, userMap, read: false, authorIdProperty: 'bucketNotesAuthorId' })
  ), [bucketTags, userMap]);
  const decoratedReadBucketTags = useMemo(() => (
    decorateTags({ tags: myReadBucketTags, userMap, read: true, authorIdProperty: 'bucketNotesAuthorId' })
  ), [myReadBucketTags, userMap]);
  const myReadEquipmentTags = useSelector(state => state.equipment.myReadEquipmentTags || []);
  const decoratedEquipmentTags = useMemo(() => (
    decorateTags({ tags: equipmentTags, userMap, read: false, authorIdProperty: 'equipmentNotesAuthorId' })
  ), [equipmentTags, userMap]);
  const decoratedReadEquipmentTags = useMemo(() => (
    decorateTags({ tags: myReadEquipmentTags, userMap, read: true, authorIdProperty: 'equipmentNotesAuthorId' })
  ), [myReadEquipmentTags, userMap]);
  const myReadVendorTags = useSelector(state => state.vendors.myReadVendorTags || []);
  const decoratedVendorTags = useMemo(() => (
    decorateTags({ tags: vendorTags, userMap, read: false, authorIdProperty: 'vendorNotesAuthorId' })
  ), [vendorTags, userMap]);
  const decoratedReadVendorTags = useMemo(() => (
    decorateTags({ tags: myReadVendorTags, userMap, read: true, authorIdProperty: 'vendorNotesAuthorId' })
  ), [myReadVendorTags, userMap]);

  const myReadCustomerTags = useSelector(state => state.customers.myReadCustomerTags || []);
  const unreadCustomerTags = useMemo(() =>
    decorateTags({ tags: myCustomerTags, userMap, read: false, authorIdProperty: 'customerNotesAuthorId' }),
  [myCustomerTags, userMap],
  );
  const readCustomerTags = useMemo(() =>
    decorateTags({ tags: myReadCustomerTags, userMap, read: true, authorIdProperty: 'customerNotesAuthorId' }),
  [myReadCustomerTags, userMap],
  );
  const decoratedUserTags = useMemo(() => (
    decorateTags({ tags: myUserTags || [], userMap, read: false, authorIdProperty: 'userNotesAuthorId' })
  ), [myUserTags, userMap]);
  const myReadUserTags = useSelector(state => state.users.myReadUserTags || []);
  const decoratedReadUserTags = useMemo(() => (
    decorateTags({ tags: myReadUserTags || [], userMap, read: true, authorIdProperty: 'userNotesAuthorId' })
  ), [myReadUserTags, userMap]);
  let content = <i key="-">You don't have any notifications</i> ;
  if (notificatonCount > 0) {
    content = (
      <Tabs tabBarStyle={{ marginTop: -10 }} onChange={onTabChanged}>
        <Tabs.TabPane tab="Unread" key="unread" style={tabStyle}>
          <NotificationsList
            key={notifications.concat(approvals)}
            notifications={notifications.filter((n) => !n.read)}
            unreadClientComms={unreadClientComms}
            approvals={approvals}
            users={users}
            markAsRead={markAsRead}
            formApprovalList={formApprovalList}
            decoratedCardTags={decoratedCardTags}
            decoratedProjectTags={decoratedProjectTags}
            decoratedProjectGroupTags={decoratedProjectGroupTags}
            decoratedBucketTags={decoratedBucketTags}
            decoratedEquipmentTags={decoratedEquipmentTags}
            decoratedVendorTags={decoratedVendorTags}
            decoratedUserTags={decoratedUserTags}
            unreadCustomerTags={unreadCustomerTags}
            markProjectTagsAsRead={markProjectTagsAsRead}
            markProjectGroupTagsAsRead={markProjectGroupTagsAsRead}
            markBucketTagsAsRead={markBucketTagsAsRead}
            markEquipmentTagsAsRead={markEquipmentTagsAsRead}
            markVendorTagsAsRead={markVendorTagsAsRead}
            markUserTagsAsRead={markUserTagsAsRead}
            onCloseDropdown={closeDropdown}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Read" key="read" style={tabStyle}>
          {
            loading
              ? <Row justify="center" align="middle"><Spin /></Row>
              : <NotificationsList
                  key={notifications.concat(approvals)}
                  notifications={notifications.filter((n) => n.read)}
                  users={users}
                  markAsRead={markAsRead}
                  decoratedCardTags={decoratedReadTags}
                  decoratedProjectTags={decoratedReadProjectTags}
                  decoratedProjectGroupTags={decoratedReadProjectGroupTags}
                  decoratedBucketTags={decoratedReadBucketTags}
                  decoratedEquipmentTags={decoratedReadEquipmentTags}
                  decoratedVendorTags={decoratedReadVendorTags}
                  decoratedUserTags={decoratedReadUserTags}
                  unreadCustomerTags={readCustomerTags}
                  markProjectTagsAsRead={markProjectTagsAsRead}
                  markProjectGroupTagsAsRead={markProjectGroupTagsAsRead}
                  markBucketTagsAsRead={markBucketTagsAsRead}
                  markEquipmentTagsAsRead={markEquipmentTagsAsRead}
                  markVendorTagsAsRead={markVendorTagsAsRead}
                  markUserTagsAsRead={markUserTagsAsRead}
                  onCloseDropdown={closeDropdown}
              />

            }
        </Tabs.TabPane>
      </Tabs>
    );
  }

  useEffect(() => {
    if (getFormApprovalSummary) getFormApprovalSummary();
  }, [getFormApprovalSummary]);

  useEffect(() => {
    if (getAssignedForms) getAssignedForms();
  }, [getAssignedForms]);

  useEffect(() => {
    if (getDrafts) getDrafts();
  }, [getDrafts]);

  useEffect(() => {
    dispatch(getMyCustomerTags());
  }, []);

  useEffect(() => {
    if (getBuckets) {
      getBuckets();
    }
  }, [getBuckets]);

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      visible={visible}
      onVisibleChange={setVisible}
    >
      <Badge count={notificatonCount || null} size="small">
        <div
          className={hasRedesign ? 'header-container-redesign header-container-bell-icon' : 'header-container'}
        >
          { hasRedesign ? <BellIcon /> : <MailOutlined /> }
        </div>
      </Badge>
    </Popover>
  );
});

// Helper function to create notification items from different data sources
const createNotificationItems = ({
  data,
  type,
  history,
  onCloseDropdown,
  markAsRead,
  bucketMap,
  equipmentMap,
  customerMap,
  users,
  initialsFromName
}) => {
  const notificationConfigs = {
    notifications: (notification) => ({
      title: notification.message,
      onClick: notification.pathname ? () => history.push({
        pathname: notification.pathname,
        state: notification.args || {},
      }) : null,
      onDelete: () => markAsRead(notification.id),
      onMouseEnter: () => {
        if (!notification.read) markAsRead(notification.id);
      },
      read: notification.read,
      type: notification.type,
      timestamp: notification.timestamp,
      key: `${notification.id}${notification.read}`,
    }),

    clientComms: (note) => ({
      title: `${note.email} has sent you a message`,
      onClick: () => {
        if (history.location.pathname !== CUSTOMER_ROUTE) {
          history.push({
            pathname: CUSTOMER_ROUTE,
            state: { targetId: note.customerId },
          });
        }
      },
      read: true,
      timestamp: note.timestamp,
      key: `client-comm-${note.customerId}`,
    }),

    formApprovals: (approval) => ({
      title: `${approval.user} has submitted a ${approval.templateName} form`,
      onClick: () => {
        if (history.location.pathname !== '/forms/log') {
          history.push('/forms/log');
        }
      },
      read: true,
      type: 'submission',
      timestamp: approval.timestamp,
      initials: initialsFromName(approval.user),
      key: `form-approval-${approval.id}`,
    }),

    cardTags: (tag) => ({
      title: `${tag.author} has tagged you in a note on ${tag.cardTitle} card on board ${tag.boardTitle}`,
      onClick: () => {
        history.push('/boards', { boardId: tag.boardId, cardId: tag.cardId, activeTab: 'details' });
        onCloseDropdown();
      },
      read: tag.read,
      type: 'submission',
      initials: initialsFromName(tag.author),
      truncate: false,
      key: `card-tag-${tag.id}`,
    }),

    projectTags: (tag) => ({
      title: `${tag.author} has tagged you in a note on project ${tag.name}`,
      onClick: () => {
        history.push('/projects', { targetId: tag.projectId, activeTab: 'details' });
        onCloseDropdown();
        if (!tag.read) {
          markAsRead([tag.projectNoteId]);
        }
      },
      onMouseEnter: () => {
        if (!tag.read) {
          markAsRead([tag.projectNoteId]);
        }
      },
      read: tag.read,
      type: 'submission',
      initials: initialsFromName(tag.author),
      truncate: false,
      key: `project-tag-${tag.id}`,
    }),

    projectGroupTags: (tag) => ({
      title: `${tag.author} has tagged you in a note on project group ${tag.name}`,
      onClick: () => {
        history.push('/projectGroups', {
          targetId: tag.projectGroupId,
          activeTab: 'details',
        });
        onCloseDropdown();
        if (!tag.read) {
          markAsRead([tag.projectGroupNoteId]);
        }
      },
      onMouseEnter: () => {
        if (!tag.read) {
          markAsRead([tag.projectGroupNoteId]);
        }
      },
      read: tag.read,
      type: 'submission',
      initials: initialsFromName(tag.author),
      truncate: false,
      key: `project-group-tag-${tag.id}`,
    }),

    bucketTags: (tag) => {
      const bucket = bucketMap[tag.bucketId];
      const bucketTemplateId = bucket?.bucketTemplateId;

      return {
        title: `${tag.author} has tagged you in a note on bucket ${tag.name}`,
        onClick: () => {
          if (bucketTemplateId) {
            history.push(`/buckets/${bucketTemplateId}`, {
              targetId: tag.bucketId,
              activeTab: 'details',
            });
          } else {
            history.push('/buckets', {
              targetId: tag.bucketId,
              activeTab: 'details',
            });
          }
          onCloseDropdown();
          if (!tag.read) {
            markAsRead([tag.bucketNoteId]);
          }
        },
        onMouseEnter: () => {
          if (!tag.read) {
            markAsRead([tag.bucketNoteId]);
          }
        },
        read: tag.read,
        type: 'submission',
        initials: initialsFromName(tag.author),
        truncate: false,
        key: `bucket-tag-${tag.id}`,
      };
    },

    equipmentTags: (tag) => {
      const equipment = equipmentMap[tag.equipmentId];

      return {
        title: `${tag.author} has tagged you in a note on equipment ${tag.name}`,
        onClick: () => {
          history.push('/equipment', {
            targetId: tag.equipmentId,
            activeTab: 'details',
          });
          onCloseDropdown();
          if (!tag.read) {
            markAsRead([tag.equipmentNoteId]);
          }
        },
        onMouseEnter: () => {
          if (!tag.read) {
            markAsRead([tag.equipmentNoteId]);
          }
        },
        read: tag.read,
        type: 'submission',
        initials: initialsFromName(tag.author),
        truncate: false,
        key: `equipment-tag-${tag.id}`,
      };
    },

    customerTags: (tag) => {
      const customer = customerMap[tag.customerId];
      const customerName = customer?.name || 'Unknown Customer';

      return {
        title: `${tag.author} has tagged you in a note on customer ${customerName}`,
        onClick: () => {
          history.push('/contacts/customers', { targetId: tag.customerId, activeTab: 'details' });
          onCloseDropdown();
          if (!tag.read) {
            markAsRead([tag.customerNoteId]);
          }
        },
        onMouseEnter: () => {
          if (!tag.read) {
            markAsRead([tag.customerNoteId]);
          }
        },
        read: tag.read,
        type: 'submission',
        initials: initialsFromName(tag.author),
        truncate: false,
        key: `customer-tag-${tag.id}`,
      };
    },

    vendorTags: (tag) => ({
      title: `${tag.author} has tagged you in a note on vendor ${tag.name}`,
      onClick: () => {
        history.push('/contacts/vendors', { targetId: tag.vendorId, activeTab: 'details' });
        onCloseDropdown();
        if (!tag.read) {
          markAsRead([tag.vendorNoteId]);
        }
      },
      onMouseEnter: () => {
        if (!tag.read) {
          markAsRead([tag.vendorNoteId]);
        }
      },
      read: tag.read,
      type: 'submission',
      initials: initialsFromName(tag.author),
      truncate: false,
      key: `vendor-tag-${tag.id}`,
    }),

    userTags: (tag) => ({
      title: `${tag.author} has tagged you in a note on user ${tag.name}`,
      onClick: () => {
        history.push('/users', { targetId: tag.userId, activeTab: 'details' });
        onCloseDropdown();
        if (!tag.read) {
          markAsRead([tag.userNoteId]);
        }
      },
      onMouseEnter: () => {
        if (!tag.read) {
          markAsRead([tag.userNoteId]);
        }
      },
      read: tag.read,
      type: 'submission',
      initials: initialsFromName(tag.author),
      truncate: false,
      key: `user-tag-${tag.id}`,
    }),

    submissions: (entry) => {
      const user = users.find(u => u.id === entry[0]);
      if (!user) return null;
      const name = user.name;
      const initials = initialsFromName(name);
      const userSubmissionCount = entry[1].length;

      return {
        title: `${name} has submitted ${userSubmissionCount} ${userSubmissionCount > 1 ? 'tasks' : 'task'}`,
        onClick: () => history.push({
          pathname: '/timecards/team',
          state: { user: user.id, submissions: true },
        }),
        type: 'submission',
        initials,
        key: `submission-${name}`,
      };
    },
  };

  const config = notificationConfigs[type];
  if (!config) return [];

  return data.map(item => {
    const notificationProps = config(item);
    if (!notificationProps) return null;

    return (
      <Row style={{ minWidth: '30em' }} key={notificationProps.key}>
        <NotificationItem {...notificationProps} />
      </Row>
    );
  }).filter(Boolean);
};

// Helper function to group submissions by user
const groupSubmissionsByUser = (approvals) => {
  const submissions = {};
  approvals.forEach(approval => {
    if (submissions[approval.userId]) {
      submissions[approval.userId].push(approval);
    } else {
      submissions[approval.userId] = [approval];
    }
  });
  return Object.entries(submissions);
};

export const NotificationsList = withRouter(({
  users = [],
  notifications = [],
  unreadClientComms = [],
  approvals = [],
  history,
  markAsRead,
  location: {
    pathname = '',
  } = {},
  formApprovalList = [],
  decoratedCardTags = [],
  decoratedProjectTags = [],
  decoratedProjectGroupTags = [],
  decoratedBucketTags = [],
  decoratedEquipmentTags = [],
  decoratedVendorTags = [],
  unreadCustomerTags = [],
  decoratedUserTags = [],
  onCloseDropdown,
  markProjectTagsAsRead,
  markProjectGroupTagsAsRead,
  markBucketTagsAsRead,
  markEquipmentTagsAsRead,
  markVendorTagsAsRead,
  markUserTagsAsRead,
}) => {
  const buckets = useSelector((state) => state.buckets.buckets);
  const bucketMap = useMemo(() => getIdMap(buckets), [buckets]);
  const equipment = useSelector((state) => state.equipment.equipment);
  const equipmentMap = useMemo(() => getIdMap(equipment), [equipment]);
  const customers = useSelector((state) => state.customers.customers);
  const customerMap = useMemo(() => customers, [customers]);

  const groupedSubmissions = useMemo(() => groupSubmissionsByUser(approvals), [approvals]);

  const notificationItems = useMemo(() => {
    const commonProps = {
      history,
      onCloseDropdown,
      bucketMap,
      equipmentMap,
      customerMap,
      users,
      initialsFromName,
    };

    return [
      ...createNotificationItems({ ...commonProps, data: notifications, type: 'notifications', markAsRead }),
      ...createNotificationItems({ ...commonProps, data: unreadClientComms, type: 'clientComms' }),
      ...createNotificationItems({ ...commonProps, data: formApprovalList, type: 'formApprovals' }),
      ...createNotificationItems({ ...commonProps, data: decoratedCardTags, type: 'cardTags', markAsRead: markAsRead }),
      ...createNotificationItems({ ...commonProps, data: decoratedProjectTags, type: 'projectTags', markAsRead: markProjectTagsAsRead }),
      ...createNotificationItems({ ...commonProps, data: decoratedProjectGroupTags, type: 'projectGroupTags', markAsRead: markProjectGroupTagsAsRead }),
      ...createNotificationItems({ ...commonProps, data: decoratedBucketTags, type: 'bucketTags', markAsRead: markBucketTagsAsRead }),
      ...createNotificationItems({ ...commonProps, data: decoratedEquipmentTags, type: 'equipmentTags', markAsRead: markEquipmentTagsAsRead }),
      ...createNotificationItems({ ...commonProps, data: unreadCustomerTags, type: 'customerTags', markAsRead: markCustomerTagsAsRead }),
      ...createNotificationItems({ ...commonProps, data: decoratedVendorTags, type: 'vendorTags', markAsRead: markVendorTagsAsRead }),
      ...createNotificationItems({ ...commonProps, data: groupedSubmissions, type: 'submissions' }),
      ...createNotificationItems({ ...commonProps, data: decoratedUserTags, type: 'userTags', markAsRead: markUserTagsAsRead }),
    ].flat();
  }, [
    notifications,
    unreadClientComms,
    formApprovalList,
    decoratedCardTags,
    decoratedProjectTags,
    decoratedProjectGroupTags,
    decoratedBucketTags,
    decoratedEquipmentTags,
    decoratedVendorTags,
    unreadCustomerTags,
    decoratedUserTags,
    history,
    onCloseDropdown,
    bucketMap,
    equipmentMap,
    customerMap,
    users,
    markAsRead,
    markProjectTagsAsRead,
    markProjectGroupTagsAsRead,
    markBucketTagsAsRead,
    markEquipmentTagsAsRead,
    markVendorTagsAsRead,
    markUserTagsAsRead,
    groupedSubmissions,
  ]);

  return notificationItems;
});
